import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Box,
  Briefcase,
  Building2,
  Check,
  ChevronRight,
  HelpCircle,
  Infinity,
  Loader2,
  Settings2,
  Sparkles,
  Timer,
  Users,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { CoreIncluded } from "@/components/modules/CoreIncluded";
import { plans as dataplans } from "@/data/plans";
import { stripePlans, type StripePlanId, planIdFromProduct } from "@/data/stripe-plans";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// ── Billing toggle ────────────────────────────────────────────
type BillingPeriod = "monthly" | "annual";

interface BillingOption {
  id: BillingPeriod;
  label: string;
  discount: number;
}

const billingOptions: BillingOption[] = [
  { id: "monthly", label: "Monatlich", discount: 0 },
  { id: "annual", label: "Jährlich", discount: 15 },
];

// ── Static plan display data ──────────────────────────────────
const planMeta: Record<
  string,
  {
    points: number | null;
    pointsLabel: string;
    icon: React.ReactNode;
    accentClass: string;
    popular: boolean;
    isEnterprise?: boolean;
    users: string;
    exampleSetup: string;
  }
> = {
  starter: {
    points: 6,
    pointsLabel: "6 Punkte",
    icon: <Zap className="h-6 w-6 text-primary" />,
    accentClass: "border-border bg-card hover:border-primary/40",
    popular: false,
    users: "Bis zu 10 Nutzer",
    exampleSetup: "z. B. Rechnungen + Angebote + Kundenkartei",
  },
  professional: {
    points: 12,
    pointsLabel: "12 Punkte",
    icon: <Sparkles className="h-6 w-6 text-accent" />,
    accentClass: "border-accent bg-card shadow-2xl shadow-accent/15 scale-[1.02] lg:-my-4",
    popular: true,
    users: "Bis zu 75 Nutzer",
    exampleSetup: "z. B. CRM + Rechnungen + Tickets + Buchhaltung",
  },
  business: {
    points: 20,
    pointsLabel: "20 Punkte",
    icon: <Briefcase className="h-6 w-6 text-success" />,
    accentClass: "border-border bg-card hover:border-primary/40",
    popular: false,
    users: "Bis zu 250 Nutzer",
    exampleSetup: "z. B. Shop + Lager + Einkauf + Rechnungen + Personal",
  },
  enterprise: {
    points: null,
    pointsLabel: "Unbegrenzt",
    icon: <Building2 className="h-6 w-6 text-primary" />,
    accentClass: "border-primary/30 bg-gradient-to-b from-card to-primary/5",
    popular: false,
    isEnterprise: true,
    users: "Unbegrenzte Nutzer",
    exampleSetup: "Alle Module + Custom-Entwicklung auf Anfrage",
  },
};

// ── How it works steps ────────────────────────────────────────
const steps = [
  {
    number: "01",
    title: "Plan wählen",
    description: "Wählen Sie ein Punkte-Budget, das zu Ihrem Unternehmen passt. Starter, Professional, Business oder Enterprise.",
    icon: <Box className="h-6 w-6" />,
  },
  {
    number: "02",
    title: "Module klicken",
    description: "Öffnen Sie den Konfigurator und wählen Sie per Klick genau die Module, die Sie brauchen – innerhalb Ihres Punktebudgets.",
    icon: <Settings2 className="h-6 w-6" />,
  },
  {
    number: "03",
    title: "Setup testen",
    description: "Starten Sie 1 Monat kostenlos. Keine Kreditkarte, keine Vertragsbindung für den Testzeitraum.",
    icon: <Timer className="h-6 w-6" />,
  },
];

// ── Comparison table ──────────────────────────────────────────
const comparisonRows = [
  {
    label: "Modul-Punkte",
    starter: "6 Punkte",
    professional: "12 Punkte",
    business: "20 Punkte",
    enterprise: "∞ Unbegrenzt",
  },
  {
    label: "Module frei kombinierbar",
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    label: "Core inklusive (DMS, E-Mail, Benutzer)",
    starter: true,
    professional: true,
    business: true,
    enterprise: true,
  },
  {
    label: "Nutzer",
    starter: "10",
    professional: "75",
    business: "250",
    enterprise: "Unbegrenzt",
  },
  {
    label: "Beispiel-Setup",
    starter: "Rechnungen + Angebote + Kunden",
    professional: "CRM + Tickets + Rechnungen + DATEV",
    business: "Shop + Lager + Einkauf + Rechnungen + HR",
    enterprise: "Alle Module + Custom",
  },
  {
    label: "Custom-Module auf Anfrage",
    starter: false,
    professional: false,
    business: false,
    enterprise: true,
  },
  {
    label: "On-Premise / Self-Hosting",
    starter: false,
    professional: false,
    business: false,
    enterprise: true,
  },
  {
    label: "Dedizierter Ansprechpartner",
    starter: false,
    professional: false,
    business: false,
    enterprise: true,
  },
];

// ── FAQ ───────────────────────────────────────────────────────
const faqs = [
  {
    question: "Wie funktionieren Punkte?",
    answer:
      "Jeder Plan enthält ein Punkte-Budget (z. B. 12 Punkte im Professional). Jedes Modul kostet 1–3 Punkte, je nach Umfang. Sie kombinieren Module frei, solange das Budget reicht – oder upgraden auf einen größeren Plan.",
  },
  {
    question: "Was ist im Core inklusive?",
    answer:
      "Dokumentenmanagement (DMS), E-Mail- & Versandkanal sowie Benutzer- und Rechteverwaltung sind in jedem Plan automatisch enthalten – ohne Punkte zu verbrauchen.",
  },
  {
    question: "Kann ich Module später ändern?",
    answer:
      "Die Modulauswahl ist Teil Ihres Setup-Prozesses. Ob und wie Module während der Laufzeit angepasst werden können, klären wir gerne im direkten Gespräch – sprechen Sie uns an.",
  },
  {
    question: "Was kostet NextInvoice?",
    answer:
      "Ab 29 € / Monat (Starter, 6 Punkte). Professional mit 12 Punkten kostet 79 € / Monat, Business mit 20 Punkten 149 € / Monat. Enterprise-Preise sind individuell. Bei längerer Laufzeit sparen Sie bis zu 15 %.",
  },
  {
    question: "Was ist der Unterschied zu klassischer Software?",
    answer:
      "Klassische ERP-Systeme liefern alles auf einmal – oft zu viel. Unser Baukasten-Prinzip erlaubt Ihnen, mit kleinen Modulen zu starten und gezielt zu erweitern, ohne Ballast.",
  },
  {
    question: "Gibt es versteckte Kosten?",
    answer:
      "Nein. Die Preise (wenn verfügbar) gelten zzgl. gesetzlicher MwSt. Keine Setup-Gebühren, keine versteckten Posten.",
  },
];

// ── Cell renderer ─────────────────────────────────────────────
function ComparisonCell({ value, accent = false }: { value: string | boolean | undefined; accent?: boolean }) {
  if (typeof value === "string") {
    return (
      <span
        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
          accent ? "bg-accent/15 text-accent" : "bg-muted text-foreground"
        }`}
      >
        {value}
      </span>
    );
  }
  if (value === true) {
    return (
      <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-success/15">
        <Check className="h-3.5 w-3.5 text-success" />
      </div>
    );
  }
  return (
    <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-muted">
      <span className="h-3.5 w-0.5 rounded-full bg-muted-foreground/40" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
const Pricing = () => {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const { user, subscription: subInfo } = useAuth();
  const navigate = useNavigate();
  const hasActiveSub = subInfo.subscribed && (subInfo.status === "active" || subInfo.status === "trialing");
  const currentPlanKey = subInfo.productId ? planIdFromProduct(subInfo.productId) : null;

  const handleCheckout = async (planId: string) => {
    if (!(planId in stripePlans)) return;
    if (!user) {
      navigate("/portal/login");
      return;
    }
    setCheckoutLoading(planId);
    try {
      const planData = stripePlans[planId as StripePlanId];
      const priceId = billingPeriod === "annual" ? planData.annual_price_id : planData.monthly_price_id;
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url && /^https:\/\//.test(data.url)) {
        window.location.href = data.url;
      }
    } catch (e: unknown) {
      console.error(e);
      toast.error("Checkout konnte nicht gestartet werden. Bitte versuchen Sie es erneut.");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const currentBilling = billingOptions.find((b) => b.id === billingPeriod)!;

  return (
    <PageLayout>
      <Helmet>
        <title>Preise & Pakete – NextInvoice ERP ab 29 €/Monat</title>
        <meta name="description" content="Starter, Professional, Business im Vergleich. Punkte-Baukasten, jährlich −15 %. Keine versteckten Kosten, monatlich kündbar – 30 Tage gratis." />
        <link rel="canonical" href="https://nxtinvoice.de/preise" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
          }))
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://nxtinvoice.de/" },
            { "@type": "ListItem", "position": 2, "name": "Preise" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "NextInvoice",
          "description": "Modulare Geschäftssoftware für KMU. Wählen Sie ein Punkte-Budget und kombinieren Sie Module nach Bedarf.",
          "brand": { "@type": "Brand", "name": "NextInvoice" },
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "EUR",
            "lowPrice": "29",
            "highPrice": "149",
            "offerCount": "3",
            "offers": [
              { "@type": "Offer", "name": "Starter",      "price": "29",  "priceCurrency": "EUR", "url": "https://nxtinvoice.de/preise", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "Professional", "price": "79",  "priceCurrency": "EUR", "url": "https://nxtinvoice.de/preise", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "Business",     "price": "149", "priceCurrency": "EUR", "url": "https://nxtinvoice.de/preise", "availability": "https://schema.org/InStock" }
            ]
          }
        })}</script>
      </Helmet>
      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-background py-20 lg:py-28">
        <div className="absolute inset-0 gradient-hero pattern-grid" />
        <div className="absolute left-0 top-0 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/8 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] translate-x-1/3 translate-y-1/3 rounded-full bg-accent/6 blur-3xl" />

        <div className="container relative">
          <div className="mx-auto max-w-4xl text-center">
            {/* Badge */}
            {!hasActiveSub && (
            <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-accent/30 bg-accent/10 px-5 py-2.5 shadow-sm">
              <Zap className="h-4 w-4 text-accent" />
              <span className="text-sm font-semibold text-accent">
                1 Monat kostenlos testen – keine Kreditkarte erforderlich
              </span>
            </div>
            )}
            {hasActiveSub && (
            <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-success/30 bg-success/10 px-5 py-2.5 shadow-sm">
              <Check className="h-4 w-4 text-success" />
              <span className="text-sm font-semibold text-success">
                Sie haben einen aktiven Plan
              </span>
            </div>
            )}

            <h1 className="mb-6 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl text-balance">
              Preise, die zu Ihrem{" "}
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Setup passen.
              </span>
            </h1>

            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-muted-foreground lg:text-xl">
              Wählen Sie ein Punkte-Budget – und klicken Sie Ihre Module selbst zusammen.
            </p>

            {/* CTAs */}
            <div className="mb-10 flex flex-wrap items-center justify-center gap-4">
              <Button
                size="lg"
                className="group h-12 bg-accent px-8 text-accent-foreground shadow-lg shadow-accent/25 hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/30 relative overflow-hidden"
                asChild
              >
                <Link to="/konfigurator">
                  <Settings2 className="mr-2 h-4 w-4" />
                  Konfigurator öffnen
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                </Link>
              </Button>
              <Button variant="ghost" size="lg" className="h-12 gap-2" asChild>
                <Link to="/konfigurator">
                  Module zusammenstellen <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            {/* Billing toggle */}
            <div className="inline-flex flex-wrap items-center justify-center gap-1.5 rounded-2xl border border-border bg-card/80 p-2 shadow-lg backdrop-blur-sm">
              {billingOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setBillingPeriod(option.id)}
                  className={`relative rounded-xl px-5 py-3 text-sm font-medium transition-all duration-300 ${
                    billingPeriod === option.id
                      ? "bg-primary text-primary-foreground shadow-lg"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  }`}
                >
                  {option.label}
                  {option.discount > 0 && (
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold ${
                        billingPeriod === option.id
                          ? "bg-primary-foreground/20 text-primary-foreground"
                          : "bg-accent/20 text-accent"
                      }`}
                    >
                      -{option.discount}%
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Trust row – neutral */}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-primary" />
                <span>Modulbaukasten</span>
              </div>
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-success" />
                <span>Startklar in Minuten</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-accent" />
                <span>B2B-fokussiert</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Cards ─────────────────────────────────────── */}
      <section className="relative bg-secondary/30 py-20 lg:py-28">
        <div className="absolute inset-0 pattern-dots opacity-30" />

        <div className="container relative">
          <div className="mx-auto grid max-w-7xl items-stretch gap-6 lg:grid-cols-4">
            {dataplans.map((plan, index) => {
              const meta = planMeta[plan.id];
              if (!meta) return null;
              const hasDiscount = currentBilling.discount > 0 && !meta.isEnterprise;

              return (
                <div
                  key={plan.id}
                  className={`group relative flex flex-col overflow-hidden rounded-2xl border-2 transition-all duration-500 ${meta.accentClass}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Popular top stripe */}
                  {meta.popular && (
                    <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-accent via-accent to-accent/60" />
                  )}
                  {meta.popular && (
                    <div className="absolute -right-10 top-7 rotate-45 bg-accent px-10 py-1.5 text-xs font-bold uppercase tracking-wider text-accent-foreground shadow-md">
                      Beliebt
                    </div>
                  )}

                  <div className="flex flex-1 flex-col p-7 lg:p-8">
                    {/* Icon + name */}
                    <div className="mb-6 flex items-center gap-3">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-xl transition-transform group-hover:scale-110 ${
                          meta.popular
                            ? "bg-accent/15 ring-2 ring-accent/20"
                            : meta.isEnterprise
                            ? "bg-primary/10 ring-2 ring-primary/20"
                            : plan.id === "business"
                            ? "bg-success/10 ring-2 ring-success/20"
                            : "bg-muted ring-2 ring-border"
                        }`}
                      >
                        {meta.icon}
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-bold text-foreground">{plan.name}</h3>
                        <p className="text-xs text-muted-foreground">{plan.tagline}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-1 flex items-baseline gap-1">
                      {meta.isEnterprise ? (
                        <span className="font-display text-3xl font-bold text-foreground">Individuell</span>
                      ) : plan.priceMonthly !== null ? (
                        <>
                          <span className="font-display text-4xl font-bold tracking-tight text-foreground">
                            {hasDiscount
                              ? Math.round(plan.priceMonthly * (1 - currentBilling.discount / 100))
                              : plan.priceMonthly}
                            &nbsp;€
                          </span>
                          <span className="text-base text-muted-foreground font-normal">/ Monat</span>
                        </>
                      ) : (
                        <span className="font-display text-3xl font-bold text-foreground">
                          Individuell
                        </span>
                      )}
                    </div>
                    <p className="mb-6 text-sm text-muted-foreground">
                      {meta.isEnterprise
                        ? "Kontaktieren Sie uns"
                        : hasDiscount
                        ? `${currentBilling.discount} % Rabatt – statt ${plan.priceMonthly} € / Monat`
                        : "zzgl. MwSt. · monatlich kündbar"}
                    </p>

                    {/* Points badge */}
                    <div
                      className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 ${
                        meta.isEnterprise ? "bg-accent/10" : "bg-primary/8 border border-primary/15"
                      }`}
                    >
                      {meta.isEnterprise ? (
                        <Infinity className="h-5 w-5 text-accent shrink-0" />
                      ) : (
                        <span className="font-display text-2xl font-bold text-primary shrink-0">
                          {meta.points}
                        </span>
                      )}
                      <div>
                        <p className="font-semibold text-sm text-foreground">
                          {meta.isEnterprise ? "Unbegrenzte Module" : "Modul-Punkte inklusive"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {meta.exampleSetup}
                        </p>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="mb-5 h-px bg-border" />

                    {/* Users */}
                    <div className="mb-5 flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4 shrink-0" />
                      {meta.users}
                    </div>

                    {/* Core included inline */}
                    <ul className="mb-6 flex flex-col gap-2">
                      {["Dokumentenmanagement (DMS)", "E-Mail & Versandkanal", "Benutzer & Berechtigungen"].map(
                        (item) => (
                          <li key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success/15">
                              <Check className="h-2.5 w-2.5 text-success" />
                            </div>
                            {item}
                          </li>
                        ),
                      )}
                      <li className="flex items-center gap-2 text-xs font-medium text-success">
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success/15">
                          <Check className="h-2.5 w-2.5 text-success" />
                        </div>
                        Core immer inklusive
                      </li>
                    </ul>

                    {/* CTA */}
                    <Button
                      size="lg"
                      className={`group/btn mt-auto relative h-14 w-full overflow-hidden text-sm font-semibold transition-all duration-300 ${
                        meta.popular
                          ? "bg-accent text-accent-foreground shadow-lg shadow-accent/25 hover:shadow-xl hover:shadow-accent/35 hover:scale-[1.02]"
                          : meta.isEnterprise
                          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:scale-[1.02]"
                          : "border-2 border-border bg-card text-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground"
                      }`}
                      variant={!meta.popular && !meta.isEnterprise ? "outline" : "default"}
                      disabled={checkoutLoading === plan.id || (hasActiveSub && currentPlanKey === plan.id)}
                      onClick={() => {
                        if (meta.isEnterprise) {
                          window.location.href = "mailto:info@nxtinvoice.de?subject=Enterprise%20Anfrage";
                        } else {
                          navigate(`/konfigurator?plan=${plan.id}`);
                        }
                      }}
                    >
                      {checkoutLoading === plan.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      {meta.isEnterprise ? "Kontakt aufnehmen" : hasActiveSub ? (currentPlanKey === plan.id ? "Ihr aktueller Plan" : "Plan wechseln") : "Kostenlos testen"}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                    </Button>

                    {/* Current plan badge */}
                    {hasActiveSub && currentPlanKey === plan.id && (
                      <p className="mt-4 text-center text-xs font-semibold text-success">
                        ✓ Ihr aktueller Plan
                      </p>
                    )}

                    {!meta.isEnterprise && !hasActiveSub && (
                      <p className="mt-4 text-center text-xs text-muted-foreground">
                        30 Tage kostenlos · danach ab {plan.priceMonthly} € / Monat
                      </p>
                    )}
                    {!meta.isEnterprise && hasActiveSub && currentPlanKey !== plan.id && (
                      <p className="mt-4 text-center text-xs text-muted-foreground">
                        {plan.priceMonthly} € / Monat
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-12 text-center text-sm text-muted-foreground">
            Alle Preise zzgl. MwSt. · Keine Setup-Gebühren{!hasActiveSub ? " · 1 Monat kostenlos testen" : ""}
          </p>
          <p className="mt-3 text-center text-xs text-muted-foreground/70">
            Nach der Bestellung erhalten Sie Ihre Portal-Zugangsdaten per E-Mail.
          </p>
        </div>
      </section>

      {/* ── Core included full ────────────────────────────────── */}
      <section className="bg-background py-16 lg:py-20">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <CoreIncluded />
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────── */}
      <section className="bg-secondary/30 py-20 lg:py-28">
        <div className="container">
          <div className="mb-14 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2">
              <Settings2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">So funktioniert der Baukasten</span>
            </div>
            <h2 className="mb-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
              In drei Schritten zum eigenen Setup
            </h2>
            <p className="mx-auto max-w-xl text-lg text-muted-foreground">
              Keine IT-Abteilung nötig. Kein langwieriges Onboarding.
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            {steps.map((step, i) => (
              <div
                key={step.number}
                className="relative flex flex-col gap-4 rounded-2xl border border-border bg-card p-8 shadow-card"
              >
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="absolute -right-3 top-1/2 hidden -translate-y-1/2 md:block">
                    <ChevronRight className="h-6 w-6 text-border" />
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <span className="font-display text-4xl font-bold text-primary/20">
                    {step.number}
                  </span>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    {step.icon}
                  </div>
                </div>

                <h3 className="font-display font-bold text-xl text-foreground">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Button
              size="lg"
              className="group h-12 bg-accent px-10 text-accent-foreground shadow-lg shadow-accent/25 hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/30 relative overflow-hidden"
              asChild
            >
              <Link to="/konfigurator">
                <Sparkles className="mr-2 h-4 w-4" />
                Jetzt konfigurieren
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Comparison table ─────────────────────────────────── */}
      <section className="bg-background py-24 lg:py-32">
        <div className="container">
          <div className="mb-14 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-2">
              <span className="text-sm font-medium text-muted-foreground">Plan-Vergleich</span>
            </div>
            <h2 className="mb-4 font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
              Was steckt in welchem Plan?
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Punkte, Module, Nutzer – alles auf einen Blick.
            </p>
          </div>

          <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-border bg-muted/70">
                    <th className="px-6 py-5 text-left font-display text-sm font-bold text-foreground">
                      Merkmal
                    </th>
                    <th className="px-4 py-5 text-center font-display text-sm font-bold text-foreground min-w-[110px]">
                      Starter
                    </th>
                    <th className="px-4 py-5 text-center font-display text-sm font-bold text-accent min-w-[110px] bg-accent/5">
                      Professional
                    </th>
                    <th className="px-4 py-5 text-center font-display text-sm font-bold text-foreground min-w-[110px]">
                      Business
                    </th>
                    <th className="px-4 py-5 text-center font-display text-sm font-bold text-primary min-w-[110px]">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, index) => (
                    <tr
                      key={row.label}
                      className={`transition-colors hover:bg-muted/40 ${index % 2 === 0 ? "bg-background" : "bg-muted/20"}`}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-foreground">
                        {row.label}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <ComparisonCell value={row.starter} />
                      </td>
                      <td className="px-4 py-4 text-center bg-accent/5">
                        <ComparisonCell value={row.professional} accent />
                      </td>
                      <td className="px-4 py-4 text-center">
                        <ComparisonCell value={row.business} />
                      </td>
                      <td className="px-4 py-4 text-center">
                        <ComparisonCell value={row.enterprise} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section className="bg-secondary/30 py-24 lg:py-32">
        <div className="container">
          <div className="mb-14 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Häufige Fragen</span>
            </div>
            <h2 className="mb-4 font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
              Noch Fragen?
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Antworten rund um das Punkte-Modell, Core und den Baukasten.
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2 lg:gap-6">
            {faqs.map((faq, index) => (
              <div
                key={faq.question}
                className="group rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:border-primary/30 hover:shadow-lg lg:p-8"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <h3 className="mb-3 font-display text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                  {faq.question}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="mb-6 text-muted-foreground">
              Weitere Fragen? Unser Team hilft Ihnen gerne weiter.
            </p>
            <Button variant="outline" size="lg" className="h-12 px-8" asChild>
              <a href="mailto:info@nxtinvoice.de?subject=Anfrage">
                Kontakt aufnehmen
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Pricing;
